/**
 * Investment Items Table Configuration
 *
 * Centralized configuration for the investment items table.
 * Follows the same pattern as helpdeskTableConfig.tsx.
 *
 * This configuration includes:
 * - Column definitions (widths, labels, sort keys, visibility)
 * - Picker field mappings (how cells map to picker modals)
 * - Sort logic (how to sort by priority, status, etc.)
 */

import {
  TableColumnsConfig,
  PickerConfig,
  TableSortConfig,
} from "shared/components/UniversalTable/types";
import { InvestmentItemStatus } from "../types/investment";

// ========== COLUMN CONFIGURATION ==========

export const INVESTMENT_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: "checkbox",
      label: "",
      minWidth: 40,
      defaultWeight: 0,
      className: "flex justify-center",
    },
    {
      id: "actions",
      label: "Acties",
      minWidth: 80,
      defaultWeight: 0,
      className: "flex justify-center",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 130,
      defaultWeight: 2,
      sortKey: "status",
      className: "flex justify-center",
    },
    {
      id: "title",
      label: "Titel",
      minWidth: 200,
      defaultWeight: 6,
      sortKey: "title",
    },
    {
      id: "label",
      label: "Label",
      minWidth: 140,
      defaultWeight: 3,
      sortKey: "label",
    },
    {
      id: "building",
      label: "Gebouw",
      minWidth: 100,
      defaultWeight: 2,
      sortKey: "building",
      visible: "lg",
    },
    {
      id: "units",
      label: "Units",
      minWidth: 120,
      defaultWeight: 2,
      visible: "lg",
    },
    {
      id: "strategicBudget",
      label: "Strategisch budget",
      minWidth: 170,
      defaultWeight: 2,
      sortKey: "strategicBudget",
    },
    {
      id: "allocatedBudget",
      label: "Gebruikt in projectonderdelen",
      minWidth: 170,
      defaultWeight: 2,
      sortKey: "allocatedBudget",
    },
    {
      id: "overflowBudget",
      label: "Verwachte overflow",
      minWidth: 170,
      defaultWeight: 2,
      sortKey: "overflowBudget",
    },
    {
      id: "createdBy",
      label: "Aangemaakt door",
      minWidth: 50,
      defaultWeight: 0,
      sortKey: "createdBy",
      visible: "lg",
      className: "flex justify-center",
    },
    {
      id: "createdAt",
      label: "Datum toegevoegd",
      minWidth: 100,
      defaultWeight: 0,
      sortKey: "createdAt",
      visible: "lg",
    },
  ],
  localStorageKey: "investment-items-column-widths",
};

// ========== PICKER FIELD CONFIGURATION ==========

export const INVESTMENT_PICKER_CONFIG: PickerConfig = {
  entityName: "item",
  entityPlural: "items",
  entityPrefix: "INV-",
  apiUpdateEndpoint: "/investment/items/{id}",

  fieldMappings: [
    // Label field (renovation labels from API)
    {
      eventMode: "label",
      pickerMode: "custom-tags",
      fieldName: "labelId",
      titleTemplate: "Selecteer label voor {count} {entityName}",
      extractCurrentValue: (row) => row.label?.id ? String(row.label.id) : null,
      buildUpdatePayload: (val) => ({ labelId: val ? parseInt(val, 10) : null }),
    },

    // Budget bucket field (strategic budget pot the line draws from).
    // customOptions are filled in dynamically per-page from the building's
    // buckets — see InvestmentItemsListPage / InvestmentItemsSection.
    {
      eventMode: "bucket",
      pickerMode: "custom-tags",
      fieldName: "bucketId",
      titleTemplate: "Selecteer strategisch budget voor {count} {entityName}",
      extractCurrentValue: (row) =>
        row.bucket?.id ? String(row.bucket.id) : null,
      buildUpdatePayload: (val) => ({
        bucketId: val ? parseInt(val, 10) : null,
      }),
      customOptions: [],
    },

    // Building field
    {
      eventMode: "building",
      pickerMode: "building",
      fieldName: "buildingId",
      titleTemplate: "Selecteer gebouw voor {count} {entityName}",
      extractCurrentValue: (row) => row.building,
      buildUpdatePayload: (val) => ({ buildingId: val?.id || null }),
    },

    // Unit field (multi-select)
    {
      eventMode: "unit",
      pickerMode: "unit",
      fieldName: "unitIds",
      titleTemplate: "Selecteer units voor {count} {entityName}",
      extractCurrentValue: (row) => row.units?.map((u: any) => u.id) || [],
      extractContextValue: (row) => row.building?.id || row.buildingId || undefined,
      buildUpdatePayload: (val) => ({ unitIds: Array.isArray(val) ? val : [] }),
      multiSelect: true,
    },

    // Title field (text input)
    {
      eventMode: "title",
      pickerMode: "text-input",
      fieldName: "title",
      titleTemplate: "Wijzig titel voor {count} {entityName}",
      extractCurrentValue: (row) => row.title,
      buildUpdatePayload: (val) => ({ title: val }),
    },

    // Estimated cost field (number input)
    {
      eventMode: "cost",
      pickerMode: "number-input",
      fieldName: "estimatedCostExclVat",
      titleTemplate: "Wijzig geschatte kosten voor {count} {entityName}",
      extractCurrentValue: (row) => row.estimatedCostExclVat,
      buildUpdatePayload: (val) => ({ estimatedCostExclVat: parseFloat(val) || 0 }),
    },

  ],

  fieldLabels: {
    status: "Status",
    labelId: "Label",
    budgetLabel: "Budget label",
    bucketId: "strategisch budget",
    buildingId: "Gebouw",
    unitIds: "Units",
    title: "Titel",
    estimatedCostExclVat: "Geschatte kosten",
  },
};

// ========== SORT CONFIGURATION ==========

export const INVESTMENT_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: "createdAt",
  defaultDirection: "desc",

  fields: [
    // Title
    {
      key: "title",
      extractor: (row) => (row.title || "").toLowerCase(),
    },

    // Label
    {
      key: "label",
      extractor: (row) => (row.label?.name || "").toLowerCase(),
    },

    // Building
    {
      key: "building",
      extractor: (row) => row.building?.id || "",
    },

    // Status
    {
      key: "status",
      extractor: (row) => {
        const statusOrder: Record<string, number> = {
          [InvestmentItemStatus.ONTWERP]: 1,
          [InvestmentItemStatus.IN_BEOORDELING]: 2,
          [InvestmentItemStatus.GOEDGEKEURD]: 3,
          [InvestmentItemStatus.AFGEWEZEN]: 4,
          [InvestmentItemStatus.IN_PROJECT]: 5,
        };
        return statusOrder[row.status] || 0;
      },
    },

    // Totaal budget — strategic + overflow (which equals max(est, allocated)
    // for both bucketed and non-bucketed lines).
    {
      key: "requiredBudget",
      extractor: (row) => {
        const est = Number(row.estimatedCostExclVat) || 0;
        const allocated = Number(row.allocatedBudget) || 0;
        return Math.max(est, allocated);
      },
    },

    // Wv. strategisch — sum of `strategicAmount` across the line's part
    // assignments (BE-persisted, FIFO over createdAt). Falls back to the
    // bucket-cap heuristic for lines not yet linked to any project part.
    {
      key: "strategicBudget",
      extractor: (row) => {
        const assignments = (row.partAssignments ?? []) as Array<{
          strategicAmount?: number;
        }>;
        if (assignments.length) {
          return assignments.reduce(
            (s, a) => s + (Number(a.strategicAmount) || 0),
            0,
          );
        }
        if (!row.bucket?.id) return 0;
        const est = Number(row.estimatedCostExclVat) || 0;
        const cap = Number(row.bucket.totalAmount) || est;
        return Math.min(est, cap);
      },
    },

    // Overflow — sum of `overflowAmount` across the line's part assignments
    // (BE-persisted, FIFO over createdAt). Same fallback as strategic.
    {
      key: "overflowBudget",
      extractor: (row) => {
        const assignments = (row.partAssignments ?? []) as Array<{
          overflowAmount?: number;
        }>;
        if (assignments.length) {
          return assignments.reduce(
            (s, a) => s + (Number(a.overflowAmount) || 0),
            0,
          );
        }
        const est = Number(row.estimatedCostExclVat) || 0;
        const allocated = Number(row.allocatedBudget) || 0;
        const strategic = row.bucket?.id
          ? Math.min(est, Number(row.bucket.totalAmount) || est)
          : 0;
        return Math.max(0, allocated - strategic);
      },
    },

    // Wv. gebruikt in projecten
    {
      key: "allocatedBudget",
      extractor: (row) => row.allocatedBudget || 0,
    },

    // Created By
    {
      key: "createdBy",
      extractor: (row) => row.createdByUserId || "",
    },

    // Created At
    {
      key: "createdAt",
      extractor: (row) => new Date(row.createdAt || "").getTime(),
    },
  ],

  caseSensitive: false,
};

// ========== COMBINED CONFIGURATION ==========

export const INVESTMENT_TABLE_CONFIG = {
  columnsConfig: INVESTMENT_COLUMNS_CONFIG,
  pickerConfig: INVESTMENT_PICKER_CONFIG,
  sortConfig: INVESTMENT_SORT_CONFIG,
};

// ========== BUILDING CONTEXT VARIANT ==========
// Used when displaying investment items within a building context (e.g., renovation tab)
// Building column is already hidden via visible: "lg" in the main config

export const INVESTMENT_BUILDING_CONTEXT_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: INVESTMENT_COLUMNS_CONFIG.columns,
  localStorageKey: "investment-building-context-column-widths",
};

export const INVESTMENT_BUILDING_CONTEXT_TABLE_CONFIG = {
  columnsConfig: INVESTMENT_BUILDING_CONTEXT_COLUMNS_CONFIG,
  pickerConfig: INVESTMENT_PICKER_CONFIG,
  sortConfig: INVESTMENT_SORT_CONFIG,
};
